import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { registerTranslationForDebug } from './translationDebugUtils';
import type {
    KeyPrefix,
    PrefixedTranslationKey,
    TranslationGroup,
    TranslationKey,
} from '../../../common/locales/types/translationTypes';
import type { TranslateOptions } from './types';

// Re-export types for backwards compatibility
export type { TranslateOptions, TranslationFunction } from './types';

/**
 * Type-safe translation function for group with optional prefix
 */
type TypedTranslationFunction<G extends TranslationGroup, P extends KeyPrefix<G> | undefined = undefined> =
    P extends KeyPrefix<G>
        ? <K extends PrefixedTranslationKey<G, P>>(
              key: K,
              defaultValue: string,
              context?: Record<string, unknown>,
              options?: TranslateOptions,
          ) => string | React.ReactElement
        : <K extends TranslationKey<G>>(
              key: K,
              defaultValue: string,
              context?: Record<string, unknown>,
              options?: TranslateOptions,
          ) => string | React.ReactElement;

/**
 * Hook to get translation function with optional type safety
 *
 * Usage:
 *   const translate = useTranslator('elements');                     // Type-checked for group
 *   const translate = useTranslator('elements', 'elementTypes.document'); // Type-checked for prefix
 */
export function useTranslator<G extends TranslationGroup, P extends KeyPrefix<G> | undefined = undefined>(
    group?: G,
    keyPrefix?: P,
): TypedTranslationFunction<G, P> {
    const translationOptions = keyPrefix ? { keyPrefix } : undefined;
    const { t } = useTranslation(group as never, translationOptions as never) as {
        t: (key: string, options?: Record<string, unknown>) => string;
    };

    const translate = useCallback(
        (
            translationKey: string,
            defaultValue: string,
            context?: Record<string, unknown>,
            options?: TranslateOptions,
        ): string | React.ReactElement => {
            // FIXME-TRANSLATIONS - Implement proper error handling at the end of the migration
            if (!group) {
                return defaultValue;
            }

            try {
                const fullTranslationKey = keyPrefix ? `${keyPrefix}.${translationKey}` : translationKey;

                // If components are provided, use Trans component for React interpolation
                if (options?.components) {
                    return (
                        <Trans
                            i18nKey={fullTranslationKey as never}
                            defaults={defaultValue}
                            values={context}
                            components={options.components}
                            ns={group as never}
                        />
                    );
                }

                const result = t(translationKey, { ...context, defaultValue });
                const translatedText = typeof result === 'string' ? result : String(result);

                registerTranslationForDebug(translatedText, group, fullTranslationKey);

                // If html option is provided, return a span with dangerouslySetInnerHTML
                if (options?.html) {
                    return (
                        <span
                            className="TranslatedString"
                            data-translation-group={group}
                            data-translation-key={fullTranslationKey}
                            dangerouslySetInnerHTML={{ __html: translatedText }}
                        />
                    );
                }

                return translatedText;
            } catch (error) {
                // FIXME-TRANSLATIONS - Implement proper error handling at the end of the migration
                return defaultValue;
            }
        },
        [t, group, keyPrefix],
    ) as TypedTranslationFunction<G, P>;

    return translate;
}
